import React, { createContext, useContext, useMemo, useState } from 'react'
import { SingleStore } from './stores.props'

type StoresContextValue = {
  cities: string[]
  selectedCity: null | string
  stores: SingleStore[]
  pins: Pin[]
  pinsToFitInMap: Pin[]
  reset: () => void
  filterByCity: (city: any) => void
  selectedStore: string | null
  filterByStoreName: (storeName: any) => void
}

type Pin = {
  lat: number
  lng: number
}

const StoresContext = createContext<StoresContextValue>({} as any)

const getPins = (stores: SingleStore[]) => {
  return stores.map((store) => ({
    lat: +store.latitude,
    lng: +store.longitude,
  }))
}

export const StoresContextProvider = ({
  children,
  stores: allStores,
}: {
  children: any
  stores: SingleStore[]
}) => {
  const [stores, setStores] = useState(allStores)

  const [selectedCity, setSelectedCity] = useState<null | string>(null)
  const [selectedStore, setSelectedStore] = useState<null | string>(null)
  const [pinsToFitInMap, setPinsToFitInMap] = useState<Pin[]>(() =>
    getPins(allStores)
  )

  const filterByCity = (city: any) => {
    const filteredStores = allStores.filter((store) => store.city === city)

    setStores(filteredStores)
    setPinsToFitInMap(getPins(filteredStores))
    setSelectedCity(city)
  }

  const filterByStoreName = (storeName: any) => {
    const filteredStores = allStores.filter((store) => store.name === storeName)
    setPinsToFitInMap(getPins(filteredStores))

    setSelectedStore(storeName)
  }

  const reset = () => {
    setStores(allStores)
    setSelectedCity(null)
    setSelectedStore(null)
    setPinsToFitInMap(getPins(allStores))
  }

  const cities = useMemo(() => {
    return [...new Set(allStores.map((store) => store.city))].sort()
  }, [allStores])

  const pins = useMemo(() => getPins(allStores), [allStores])

  return (
    <StoresContext.Provider
      value={{
        stores,
        cities,
        pins,
        reset,
        filterByCity,
        selectedCity,
        pinsToFitInMap,
        selectedStore,
        filterByStoreName,
      }}
    >
      {children}
    </StoresContext.Provider>
  )
}

export const useStoresContext = () => {
  return useContext(StoresContext)
}

export const useStores = () => {
  return useContext(StoresContext).stores
}

export const useStoresPins = () => {
  return useContext(StoresContext).pins
}

export const useStoresPinsToFitInView = () => {
  return useContext(StoresContext).pinsToFitInMap
}
